import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";
import { COMPANY } from "@/data/site";
import usePageMeta from "@/hooks/usePageMeta";
import { Shell } from "./Legal";

const MODES = {
  retractation: {
    title: "Se rétracter du contrat", endpoint: "/public/retractations",
    meta: "Exercez gratuitement votre droit de rétractation de 14 jours pour un contrat conclu à distance avec Conciergerie Familly 29.",
    intro: "Client consommateur, vous disposez de 14 jours à compter de la conclusion d'un contrat de prestation de services conclu à distance ou hors établissement pour vous rétracter, sans motif (article 17 des CGV). Remplissez le formulaire ci-dessous : vous recevrez immédiatement un accusé de réception horodaté par e-mail.",
    confirm: "Je confirme ma décision de me rétracter du contrat identifié ci-dessus.",
    submit: "Confirmer ma rétractation", dateLabel: "Date de conclusion du contrat",
  },
  resiliation: {
    title: "Résilier votre contrat", endpoint: "/public/resiliations",
    meta: "Résiliez gratuitement en ligne votre abonnement ou contrat Conciergerie Familly 29 (préavis de 30 jours).",
    intro: "Vous pouvez résilier gratuitement votre abonnement ou contrat à durée indéterminée, avec un préavis de 30 jours (article 16 des CGV). Les prestations exécutées et dépenses autorisées jusqu'à la date d'effet restent dues. Vous recevrez un accusé de réception indiquant la date de fin et les effets de la résiliation.",
    confirm: "Je confirme ma demande de résiliation du contrat identifié ci-dessus.",
    submit: "Confirmer la résiliation", dateLabel: "Date de souscription (si connue)",
  },
};

const F = ({ label, required, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}{required && " *"}</Label>{children}</div>
);

const Receipt = ({ r, m }) => (
  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-8" data-testid="legal-request-receipt">
    <CheckCircle2 className="h-10 w-10 text-emerald-700" />
    <h2 className="mt-3 font-display text-2xl">Demande enregistrée</h2>
    <p className="mt-2 text-sm text-slate-700">Accusé de réception n° <b data-testid="receipt-id">{r.reference}</b> – reçu le <b data-testid="receipt-date">{new Date(r.created_at).toLocaleString("fr-FR")}</b>.</p>
    {r.date_effet && <p className="mt-1 text-sm text-slate-700">Date de fin du contrat (préavis de 30 jours) : <b data-testid="receipt-effet">{new Date(r.date_effet).toLocaleDateString("fr-FR")}</b>.</p>}
    <p className="mt-2 text-sm text-slate-700">Une copie de cet accusé ({m.title.toLowerCase()}) a été envoyée à votre adresse e-mail. Conservez-la.</p>
  </div>
);

export default function LegalRequest({ mode }) {
  const m = MODES[mode];
  usePageMeta(m.title, m.meta);
  const init = { reference: "", prestation: "", date_contrat: "", nom: "", adresse: "", email: "", message: "", confirmation: false };
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.confirmation) return toast.error("Merci de confirmer votre décision.");
    setBusy(true);
    try {
      const { data } = await api.post(m.endpoint, f);
      toast.dismiss();
      setReceipt(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title={m.title} testid={`${mode}-page`}>
      <p className="text-sm leading-relaxed text-slate-700">{m.intro}</p>
      <p className="mt-3 text-xs text-slate-500">Vous pouvez aussi adresser toute déclaration dénuée d'ambiguïté à {COMPANY.email} ou par courrier à {COMPANY.dirigeant} EI – {COMPANY.nomLegal}, {COMPANY.siege}. <Link to="/cgv" className="underline">Voir les CGV</Link>.</p>
      <div className="mt-8">
        {receipt ? <Receipt r={receipt} m={m} /> : (
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid={`${mode}-form`}>
            <F label="Référence (devis, commande ou abonnement)" required><Input required value={f.reference} onChange={set("reference")} className="h-11" data-testid={`${mode}-reference-input`} /></F>
            <F label={m.dateLabel} required={mode === "retractation"}><Input type="date" required={mode === "retractation"} value={f.date_contrat} onChange={set("date_contrat")} className="h-11" data-testid={`${mode}-date-input`} /></F>
            <div className="sm:col-span-2"><F label="Prestation ou formule concernée" required><Input required value={f.prestation} onChange={set("prestation")} className="h-11" data-testid={`${mode}-prestation-input`} /></F></div>
            <F label="Nom et prénom" required><Input required value={f.nom} onChange={set("nom")} className="h-11" data-testid={`${mode}-nom-input`} /></F>
            <F label="E-mail utilisé pour la commande" required><Input required type="email" value={f.email} onChange={set("email")} className="h-11" data-testid={`${mode}-email-input`} /></F>
            <div className="sm:col-span-2"><F label="Adresse postale" required><Input required value={f.adresse} onChange={set("adresse")} className="h-11" data-testid={`${mode}-adresse-input`} /></F></div>
            <div className="sm:col-span-2"><F label="Précisions (facultatif)"><Textarea rows={3} value={f.message} onChange={set("message")} data-testid={`${mode}-message-input`} /></F></div>
            <label className="flex items-start gap-3 text-sm text-slate-700 sm:col-span-2">
              <Checkbox checked={f.confirmation} onCheckedChange={(v) => set("confirmation")(!!v)} className="mt-0.5" data-testid={`${mode}-confirm-checkbox`} />
              <span>{m.confirm} *</span>
            </label>
            <p className="text-xs text-slate-500 sm:col-span-2">Vos données servent uniquement au traitement de cette demande et à sa preuve (<Link to="/confidentialite" className="underline">politique de confidentialité</Link>).</p>
            <div className="sm:col-span-2">
              <button type="submit" disabled={busy} data-testid={`${mode}-submit-btn`}
                className="inline-flex h-12 items-center gap-2 rounded-full bg-[#D82C2C] px-8 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#b82323] disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}{m.submit}
              </button>
            </div>
          </form>
        )}
      </div>
    </Shell>
  );
}
